import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  NOT_CONFIGURED,
  TOO_MANY_REQUESTS,
  type AccessRequestBody,
  type AccessRequestFiled,
  type AccessRoute,
  type SystemId,
} from '@revenexx/backstage-plugin-access-common';

/**
 * The Access page's two questions to the portal's `access` backend: who a
 * request goes to, and filing it.
 *
 * The panel never works the approver out itself. The backend does, in the same
 * function it files with, so the team the panel names is the team the issue
 * lands in (specs/access-request.md AC-1).
 */

export type Route = AccessRoute;

export type RouteState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; route: Route }
  | { status: 'error' };

/** How long a typed target settles before the backend is asked. */
const SETTLE_MS = 300;

export function useRoute(system: SystemId, target: string): RouteState {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<RouteState>({ status: 'idle' });

  useEffect(() => {
    if (!target.trim()) {
      setState({ status: 'idle' });
      return undefined;
    }
    let active = true;
    setState({ status: 'loading' });
    const timer = window.setTimeout(async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('access');
        const query = new URLSearchParams({ system, target });
        const res = await fetchApi.fetch(`${baseUrl}/route?${query}`);
        if (!res.ok) throw new Error(`${res.status}`);
        const route = (await res.json()) as Route;
        if (active) setState({ status: 'ready', route });
      } catch {
        if (active) setState({ status: 'error' });
      }
    }, SETTLE_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [discoveryApi, fetchApi, system, target]);

  return state;
}

export type SendOutcome =
  | { status: 'sent'; issue: { identifier: string; url: string }; route: Route }
  | { status: 'not-configured' }
  | { status: 'rate-limited' }
  | { status: 'failed' };

export type { AccessRequestBody };

export function useSendRequest(): (
  body: AccessRequestBody,
) => Promise<SendOutcome> {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return async body => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('access');
      const res = await fetchApi.fetch(`${baseUrl}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 503) {
        const answer = (await res.json().catch(() => ({}))) as {
          code?: string;
        };
        if (answer.code === NOT_CONFIGURED) return { status: 'not-configured' };
        return { status: 'failed' };
      }
      if (res.status === 429) {
        const answer = (await res.json().catch(() => ({}))) as {
          code?: string;
        };
        if (answer.code === TOO_MANY_REQUESTS)
          return { status: 'rate-limited' };
      }
      if (!res.ok) return { status: 'failed' };
      const answer = (await res.json()) as AccessRequestFiled;
      return { status: 'sent', issue: answer.issue, route: answer.route };
    } catch {
      return { status: 'failed' };
    }
  };
}
